// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import {
  useCycleEvents,
  useCycleLatestSnapshot,
} from "@/queries/queries.eventJournal"
import { useParsedLatestProgress } from "../_useParsedLatestProgress"
import { selectInitPhaseNarration, selectPhaseLabel } from "./narration"

/**
 * `PhaseNarration` — the pre-snapshot phase line (issue
 * `gen0-milestone-narration`, US §3/§4). Narrates the generation-0 dead
 * zone from the already-relevant milestone feed: "Initializing
 * population…" while pop-init is started-but-not-completed and no
 * snapshot exists, then nothing once initialization completes or the
 * first snapshot lands (the live panels narrate from there).
 *
 * Reads its own two sources (the relevant events feed + the latest
 * snapshot) and renders nothing when there is no narration to show — so
 * a snapshot-present or a completed-init cycle carries no leftover phase
 * banner.
 */
export interface PhaseNarrationProps {
  cycleId: string
}

export default function PhaseNarration({ cycleId }: PhaseNarrationProps) {
  const eventsQuery = useCycleEvents({ cycleId })
  const snapshotQuery = useCycleLatestSnapshot(cycleId)
  const { progress } = useParsedLatestProgress(cycleId)

  const eventNames = (eventsQuery.data?.items ?? []).map((e) => e.event_name)
  const hasSnapshot = snapshotQuery.isSuccess && snapshotQuery.data != null

  // The live phase supersedes the milestone narration once progress rows
  // exist; the milestone path stays as the pre-progress fallback.
  const narration =
    selectPhaseLabel(progress) ??
    selectInitPhaseNarration(eventNames, hasSnapshot)
  if (narration == null) return null

  return (
    <div
      role="status"
      aria-label="Cycle phase"
      className="flex items-center gap-2 rounded-[10px] border border-primary/25 bg-primary/[0.06] px-3.5 py-2 font-mono text-[12px] text-text-secondary"
    >
      <span
        aria-hidden
        className="size-1.5 animate-pulse rounded-full bg-primary"
        style={{ boxShadow: "0 0 7px currentColor" }}
      />
      {narration}
    </div>
  )
}
